import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GlobalService } from '../../../service/global.service';
import { DASHBOARD_MENU, DashboardMenuGroup } from '../dashboard-menu';

@Component({
  selector: 'app-dashboard-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard-sidebar.component.html',
  styleUrl: './dashboard-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardSidebarComponent implements OnInit, OnDestroy {
  /** When true the sidebar renders as a narrow icon-only rail. */
  @Input() collapsed = false;

  /** Emitted when a link is tapped — lets the layout close the mobile drawer. */
  @Output() navigate = new EventEmitter<void>();

  readonly groups: DashboardMenuGroup[] = DASHBOARD_MENU;

  /** Current route path (query params/fragment stripped) used to highlight the active item. */
  activePath = '';
  private routerSub?: Subscription;

  constructor(public gS: GlobalService, private router: Router, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.activePath = this.normalize(this.router.url);
    // OnPush won't re-check this sidebar on its own when only the route changes,
    // so recompute the active item on every navigation and mark for check.
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.activePath = this.normalize(e.urlAfterRedirects);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  /** True when the given menu route matches the current URL (ignores query params). */
  isActive(route: string): boolean {
    const target = this.normalize('/' + route);
    return this.activePath === target || this.activePath.startsWith(target + '/');
  }

  private normalize(url: string): string {
    const path = (url || '').split('?')[0].split('#')[0];
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  get initial(): string {
    return (this.gS.userName || 'U').toString().trim().charAt(0).toUpperCase();
  }

  onNavigate(): void {
    this.navigate.emit();
  }

  logout(): void {
    this.gS.LogoutUser();
  }
}
